import { memo, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { TopUser } from "../types"

import { Users } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { EmptyState } from "@/components/ui/empty-state"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SortableTableHeader } from "@/components/ui/sortable-table-header"
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table"

import { getEmptyStates } from "./admin-dashboard-empty.utils"
import { MobileSortControls } from "./mobile-sort-controls"

const INITIAL_VISIBLE_COUNT = 10
const ALL_VALUE = "__all__"

export const TopUsersTable = memo(function TopUsersTable({ users }: { users: TopUser[] }) {
  const { t } = useTranslation()
  const EMPTY_STATES = getEmptyStates(t)
  const [sort, setSort] = useState<{ key: string | null; direction: "asc" | "desc" | null }>({
    key: "tokens",
    direction: "desc",
  })
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT)
  const [companyFilter, setCompanyFilter] = useState<string | null>(null)
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null)

  const companies = useMemo(() => {
    const set = new Set<string>()
    users.forEach((u) => {
      if (u.company) set.add(u.company)
    })
    return Array.from(set).sort()
  }, [users])

  const departments = useMemo(() => {
    const set = new Set<string>()
    users.forEach((u) => {
      if (u.department) set.add(u.department)
    })
    return Array.from(set).sort()
  }, [users])

  const filteredUsers = useMemo(() => {
    let result = users
    if (companyFilter) {
      result = result.filter((u) => u.company === companyFilter)
    }
    if (departmentFilter) {
      result = result.filter((u) => u.department === departmentFilter)
    }
    return result
  }, [users, companyFilter, departmentFilter])

  const sortedUsers = useMemo(() => {
    if (!sort.key || !sort.direction) {
      return filteredUsers
    }

    const sorted = [...filteredUsers].sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sort.key) {
        case "user":
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case "chats":
          aValue = a.chats
          bValue = b.chats
          break
        case "tokens":
          aValue = a.tokens
          bValue = b.tokens
          break
        default:
          return 0
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return sort.direction === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number)
    })

    return sorted
  }, [filteredUsers, sort])

  const visibleUsers = useMemo(() => {
    return sortedUsers.slice(0, visibleCount)
  }, [sortedUsers, visibleCount])

  const hasMore = sortedUsers.length > visibleCount
  const isExpanded = visibleCount > INITIAL_VISIBLE_COUNT && visibleCount >= sortedUsers.length

  const handleShowMore = () => {
    setVisibleCount(sortedUsers.length)
  }

  const handleShowLess = () => {
    setVisibleCount(INITIAL_VISIBLE_COUNT)
  }

  const handleSort = (key: string) => {
    setSort((prev) => {
      if (prev.key === key) {
        if (prev.direction === "asc") {
          return { key, direction: "desc" }
        }
        if (prev.direction === "desc") {
          return { key: null, direction: null }
        }
        return { key, direction: "asc" }
      }
      return { key, direction: "asc" }
    })
  }

  const hasFilters = companies.length > 0 || departments.length > 0

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="size-5" />
          {t("adminDashboard.tables.topUsers.title")}
        </CardTitle>
        {hasFilters && (
          <div className="flex flex-wrap gap-2 pt-2">
            {companies.length > 0 && (
              <Select
                value={companyFilter ?? ALL_VALUE}
                onValueChange={(v) => {
                  setCompanyFilter(v === ALL_VALUE ? null : v)
                  setVisibleCount(INITIAL_VISIBLE_COUNT)
                }}
              >
                <SelectTrigger className="h-8 w-[180px] text-xs">
                  <SelectValue
                    placeholder={t(
                      "adminDashboard.tables.topUsers.filters.allCompanies",
                      "All companies"
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>
                    {t("adminDashboard.tables.topUsers.filters.allCompanies", "All companies")}
                  </SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {departments.length > 0 && (
              <Select
                value={departmentFilter ?? ALL_VALUE}
                onValueChange={(v) => {
                  setDepartmentFilter(v === ALL_VALUE ? null : v)
                  setVisibleCount(INITIAL_VISIBLE_COUNT)
                }}
              >
                <SelectTrigger className="h-8 w-[180px] text-xs">
                  <SelectValue
                    placeholder={t(
                      "adminDashboard.tables.topUsers.filters.allDepartments",
                      "All departments"
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>
                    {t("adminDashboard.tables.topUsers.filters.allDepartments", "All departments")}
                  </SelectItem>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-2 lg:hidden">
          <MobileSortControls
            ariaLabel="Sort top users"
            direction={sort.direction}
            sortKey={sort.key}
            options={[
              { id: "user", label: t("adminDashboard.tables.topUsers.headers.user") },
              { id: "chats", label: t("adminDashboard.tables.topUsers.headers.chats") },
              { id: "tokens", label: t("adminDashboard.tables.topUsers.headers.tokens") },
            ]}
            onSortChange={(key, direction) => setSort({ key, direction })}
          />
          {visibleUsers.length === 0 ? (
            <EmptyState
              description={EMPTY_STATES.topUsers.description}
              icon={Users}
              title={EMPTY_STATES.topUsers.title}
            />
          ) : (
            visibleUsers.map((user, index) => (
              <div
                key={`${user.name}-mobile-${index}`}
                className="bg-card space-y-3 rounded-lg border p-3 text-sm"
                data-testid="top-user-mobile-row"
              >
                <div className="min-w-0">
                  <div className="font-medium break-all">{user.name}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {user.company ? (
                      <Badge className="text-[10px] font-normal" variant="secondary">
                        {user.company}
                      </Badge>
                    ) : null}
                    {user.department ? (
                      <Badge className="text-[10px] font-normal" variant="outline">
                        {user.department}
                      </Badge>
                    ) : null}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-muted-foreground block">Chats</span>
                    <span className="font-semibold tabular-nums">
                      {user.chats.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">Tokens</span>
                    <span className="font-semibold tabular-nums">
                      {user.tokens.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHeader
                  align="left"
                  className="h-12 min-w-[200px]"
                  currentSort={sort}
                  sortKey="user"
                  onSort={handleSort}
                >
                  {t("adminDashboard.tables.topUsers.headers.user")}
                </SortableTableHeader>
                <SortableTableHeader
                  align="right"
                  className="h-12 w-24"
                  currentSort={sort}
                  sortKey="chats"
                  onSort={handleSort}
                >
                  {t("adminDashboard.tables.topUsers.headers.chats")}
                </SortableTableHeader>
                <SortableTableHeader
                  align="right"
                  className="h-12 min-w-[120px]"
                  currentSort={sort}
                  sortKey="tokens"
                  onSort={handleSort}
                >
                  {t("adminDashboard.tables.topUsers.headers.tokens")}
                </SortableTableHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.length === 0 ? (
                <TableRow>
                  <TableCell className="h-32" colSpan={4}>
                    <EmptyState
                      description={EMPTY_STATES.topUsers.description}
                      icon={Users}
                      title={EMPTY_STATES.topUsers.title}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                visibleUsers.map((user, index) => (
                  <TableRow key={`${user.name}-${index}`}>
                    <TableCell className="min-w-[200px]">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium break-all">{user.name}</span>
                        {user.company && (
                          <Badge className="text-[10px] font-normal" variant="secondary">
                            {user.company}
                          </Badge>
                        )}
                        {user.department && (
                          <Badge className="text-[10px] font-normal" variant="outline">
                            {user.department}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="w-24 text-right tabular-nums">
                      {user.chats.toLocaleString()}
                    </TableCell>
                    <TableCell className="min-w-[120px] text-right tabular-nums">
                      {user.tokens.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {hasMore && (
          <div className="mt-4">
            <Button className="w-full" variant="outline" onClick={handleShowMore}>
              {t("adminDashboard.tables.topUsers.showMore", "Show More")} (
              {sortedUsers.length - visibleCount}{" "}
              {t("adminDashboard.tables.topUsers.moreUsers", "more")})
            </Button>
          </div>
        )}
        {isExpanded && (
          <div className="mt-4">
            <Button className="w-full" variant="outline" onClick={handleShowLess}>
              {t("adminDashboard.tables.topUsers.showLess", "Show Less")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
})
